/**
 * Ein Drawer zum Anlegen und Bearbeiten — fuer die ganze Anwendung (C0).
 *
 * Woertlich uebernommen aus dem Entwurf: zentriertes Fenster ueber
 * abgedunkeltem Hintergrund, an der rechten unteren Ecke stufenlos ziehbar,
 * Feldgruppen im Zweispaltenraster, Fehlerzusammenfassung mit Sprunglinks.
 *
 * Geoeffnet wird er ueber `useDrawer().oeffnen(schluessel, vorbelegung,
 * bearbeiten)`; der Katalog der Formulare steht in `entwurf/formulare.ts`.
 *
 * Abweichung vom Entwurf, bewusst und vermerkt: die Vorlage laedt Markenlogos
 * von `cdn.simpleicons.org`. Ein Abruf bei Dritten aus einem Werkzeug, das
 * Bestandsdaten fuehrt, ist eine Freigabeentscheidung — hier erscheint deshalb
 * ausschliesslich das lokal erzeugte Monogramm oder ein hochgeladenes Bild.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { stil } from './stil';
import { erklaerung } from '../entwurf/marker';
import { useToast } from './Toast';
import {
  FORMULARE, anfangswerte, feldFehler, feldSichtbar, formularFuerModule, optionenFuer, vorgabeFuer, type Feld, type Formular,
} from '../entwurf/formulare';
import { bestandGeaendert, useListe, useWerte } from '../api/haken';
import { useModule } from './Modulrahmen';
import { mandantAusSpeicher } from '../entwurf/mandant';
import { DEMO, DEMO_HINWEIS } from '../entwurf/demo.js';
import {
  ABBILDUNGEN, assetTypAusFormular, kategorieAusFormular, namensfeldAusFormular, vorbelegungAusSatz,
} from '../api/anlegen';
import { holen } from '../api/klient';
import { tokenHolen } from '../anmeldung/msal';
import { useFremdaenderung, type Beobachtet } from '../api/live';
import {
  LEERER_PLAN, Rollenzuordnung, planBelegt, planFehler, type Rollenplan,
} from './Rollenzuordnung';
import { Ersetzenweg } from './Ersetzenweg';
import { Livehinweis } from './Livehinweis';
import { Konfliktleiste, useKonfliktweg, type Konflikt } from './Konfliktweg';

/* ------------------------------------------------------------- Monogramm */

/** Stabiler Hashwert 0…1 — im Entwurf `hash`, hier fuer den Farbton. */
function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/** Monogramm aus dem Namen (Anforderung 27) — zwei Anfangsbuchstaben. */
export function monogramm(name: string): { text: string; flaeche: string } {
  const teile = String(name || '').replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/);
  const erst = teile[0] || '?';
  const zweit = teile[1] || '';
  const text = ((erst[0] ?? '?') + (zweit[0] ?? '')).toUpperCase();
  const h = Math.floor(hash(name + 'lg') * 360);
  return { text, flaeche: 'oklch(0.55 0.11 ' + h + ')' };
}

/* ------------------------------------------------------------- Kontext */

/** Was ein Asset in den Zuordnungen ausmacht: Name, Objektart, Kategorie. */
export interface AssetKennung { name: string; assetTyp: string; kategorie: string }

/**
 * Welcher Satz bearbeitet wird — Schluessel und Zeilenversion, bei Assets
 * dazu Name und Objektart aus dem **gespeicherten** Satz.
 *
 * Die Objektart steht nicht im Formular (`assetTyp` ist `nurAnlegen`), und
 * eine mobile App wird seit dem 07.10.2026 im Formular der Applikation
 * bearbeitet. Wer ihre Zuordnungen sucht, braucht deshalb die Objektart des
 * Satzes, nicht die des Formulars.
 */
export interface Bezug {
  schluessel: string;
  version: string;
  asset?: AssetKennung;
}

/** Was neben den Feldern an `onSpeichern` geht. */
export interface Speicherzusatz {
  /** Die gewählten Rollen — nur bei Assets, sonst `null`. */
  rollen: Rollenplan | null;
  /** Steht in einem Feld etwas anderes als beim Öffnen? */
  felderGeaendert: boolean;
}

/**
 * Antwort von `onSpeichern`, wenn das Fenster offen bleibt und sich dabei
 * etwas ändert.
 *
 * - `offen` mit `bezug`: der Satz steht inzwischen (angelegt oder mit neuer
 *   Version), aber ein zweiter Schritt — die Rollen — scheiterte. Das
 *   Fenster wechselt in den Bearbeiten-Modus. Ein zweites „Speichern" legte
 *   sonst ein zweites Asset an.
 * - `konflikt`: die Schnittstelle meldete 409. Der Drawer zeigt den
 *   Konfliktweg — mit Neuladen statt nur einer Meldung.
 */
export type Speicherantwort =
  | { offen: true; bezug?: Bezug; rollen?: Rollenplan }
  | { konflikt: unknown };

/** Wie ein Asset im Satz heißt — „dieser Applikation", „eine andere Applikation". */
const ART_TEXT: Record<string, { dieser: string; andere: string }> = {
  Applikation: { dieser: 'dieser Applikation', andere: 'eine andere Applikation' },
  MobileApp: { dieser: 'dieser Applikation', andere: 'eine andere Applikation' },
  Fileserver: { dieser: 'dieses Laufwerks', andere: 'ein anderes Laufwerk' },
  Postfach: { dieser: 'dieses Postfachs', andere: 'ein anderes Postfach' },
  Verteiler: { dieser: 'dieses Verteilers', andere: 'einen anderen Verteiler' },
  M365: { dieser: 'dieser Gruppe', andere: 'eine andere Gruppe' },
  Hardware: { dieser: 'dieser Hardware', andere: 'eine andere Hardware' },
};
const artText = (assetTyp: string | undefined) =>
  ART_TEXT[assetTyp ?? ''] ?? { dieser: 'dieses Assets', andere: 'ein anderes Asset' };

/** Objektart im Änderungsprotokoll je Sammlung — für Konflikt und Live-Hinweis. */
const OBJEKTART: Record<string, string> = {
  assets: 'Asset', gruppen: 'Gruppe', zuordnungen: 'Zuordnung', 'offene-punkte': 'OffenerPunkt',
};

interface DrawerZustand {
  key: string;
  werte: Record<string, string>;
  /**
   * Die Werte beim Oeffnen. Nur gegen sie laesst sich sagen, ob jemand etwas
   * eingegeben hat.
   *
   * Vorher wurde gegen „leer" geprueft — und `anfangswerte` belegt jedes
   * Auswahlfeld mit seiner ersten Option. Alle acht Formulare haben mindestens
   * eines, also war „ist befuellt" direkt nach dem Oeffnen wahr und die
   * Rueckfrage kam immer. Wer ein Formular versehentlich oeffnete und sofort
   * schloss, musste eine Warnung wegklicken, die auf nichts zeigte.
   */
  anfang: Record<string, string>;
  fehler: Record<string, string | undefined>;
  bearbeiten: boolean;
  /**
   * Welcher Satz bearbeitet wird — Schluessel und Zeilenversion.
   *
   * Ohne diese beiden Angaben kann der Schreibweg nur **anlegen**: er weiss
   * sonst nicht, welche Zeile gemeint ist, und ohne Version liesse sich nicht
   * sagen, ob inzwischen jemand anders geschrieben hat.
   *
   * Genau das war bis zum 28.08.2026 der Fall. `bearbeiten` gab es, der
   * Bezug nicht — und `Anlegeweg.tsx` legte deshalb bei jedem „Aenderungen
   * speichern" einen **neuen** Datensatz an. Der Kunde hat es am Warenkorb
   * gemerkt; betroffen war jedes Feld jedes Assets.
   */
  bezug: Bezug | null;
  /** Die Rueckfrage vor dem Verwerfen steht im Fuss des Formulars. */
  nachfrage: boolean;
  /** Die gewählten Rollen — nur bei Assets von Belang. */
  rollen: Rollenplan;
  /** Steht statt des Formulars „Ersetzen durch …"? */
  ersetzen: boolean;
}

interface DrawerApi {
  /** Oeffnet ein Formular aus dem Katalog. */
  oeffnen: (
    key: string,
    vorbelegung?: Record<string, string> | null,
    bearbeiten?: boolean,
    /** Nur beim Bearbeiten: welcher Satz, mit welcher Zeilenversion. */
    bezug?: Bezug | null,
  ) => void;
}

/** Formularfelder, die einen Verantwortlichen tragen. */
const OWNERFELDER = new Set(['owner', 'owner1', 'owner2', 'fachlicherOwner']);

/**
 * Woher die Vorschläge je Formular kommen.
 *
 * Aus der **eigenen** Sammlung und nicht aus `personen`: der Owner einer
 * Applikation muss keine erfasste Person sein — er steht als Name am Asset.
 * Bei HOWOGE gibt es `personen` überdies gar nicht, und ein Formular, dessen
 * Vorschläge beim einen Mandanten kommen und beim anderen nicht, wäre
 * schwerer zu erklären als eines ohne.
 */
const OWNERQUELLE: Record<string, { sammlung: string; feld: string }> = {
  applikation: { sammlung: 'assets', feld: 'ownerName' },
  laufwerk: { sammlung: 'assets', feld: 'ownerName' },
  postfach: { sammlung: 'assets', feld: 'ownerName' },
  team: { sammlung: 'assets', feld: 'ownerName' },
  hardware: { sammlung: 'assets', feld: 'ownerName' },
  gruppe: { sammlung: 'gruppen', feld: 'owner' },
};

const DrawerKontext = createContext<DrawerApi>({ oeffnen: () => {} });

export function useDrawer(): DrawerApi {
  return useContext(DrawerKontext);
}

/* ------------------------------------------------------------- Baustein */

export interface DrawerProps {
  children: ReactNode;
  /**
   * Auswahl der Organisationseinheiten — im Entwurf kommen sie aus den
   * Echtdaten und fuellen das Feld `oeCode`.
   */
  organisationseinheiten?: string[];
  /**
   * Wird nach erfolgreicher Pruefung gerufen; ohne Angabe bleibt es bei der
   * Meldung.
   *
   * **Der Rueckgabewert entscheidet, ob das Fenster schliesst.** `false`
   * heisst: es blieb etwas zu tun, das Formular bleibt mit allen Eingaben
   * stehen. Alles andere — auch `undefined` — schliesst wie bisher.
   *
   * Der Kunde hat es am 27.08.2026 gemeldet: „Wenn man etwas fehlerhaft
   * eingibt, wird das Fenster mit allen Eingaben verworfen und man bekommt
   * die Fehlermeldung. Besser waere es, wenn der Reject in der Maske kommt,
   * man den Fehler fixen kann und dann speichern." Genau so ist es jetzt.
   */
  onSpeichern?: (
    key: string, werte: Record<string, string>, bearbeiten: boolean,
    bezug: Bezug | null,
    /** Seit dem 07.10.2026: die gewählten Rollen eines Assets. */
    zusatz: Speicherzusatz,
  ) => void | boolean | Speicherantwort | Promise<void | boolean | Speicherantwort>;
}

/** Hat jemand im Formular oder an den Rollen etwas geändert? */
function istGeaendert(st: DrawerZustand): boolean {
  return Object.keys(st.werte).some((k) => st.werte[k] !== st.anfang[k]) || planBelegt(st.rollen);
}

export function Drawer({ children, organisationseinheiten, onSpeichern }: DrawerProps) {
  /* Der Zuschnitt des Mandanten. Der Drawer haengt **ueber** `App` und kann
     ihn deshalb nicht als Prop bekommen; `Modulrahmen` reicht ihn eine Ebene
     nach oben. Ohne ihn blendet nichts aus — nie weniger, als einem zusteht. */
  /* Laeuft gerade ein Schreibvorgang? Sperrt die beiden Speicherknoepfe,
     damit nichts zweimal abgeschickt wird, solange das Fenster offen bleibt. */
  const [laeuft, setzeLaeuft] = useState(false);
  const module = useModule();
  const formularVon = useCallback(
    (key: string) => {
      const kat = FORMULARE[key];
      return kat ? formularFuerModule(kat, module) : undefined;
    },
    [module],
  );

  const [zustand, setzeZustand] = useState<DrawerZustand | null>(null);
  const melden = useToast();
  const fenster = useRef<HTMLElement>(null);

  /* Organisationseinheiten holt der Drawer selbst.
     `main.tsx` haengt ihn ueber `<App />` ein und reicht nichts weiter; das
     Feld `oeCode` im Formular „Offener Punkt" war dadurch ein `<select>` ohne
     eine einzige Option — anklickbar, aber leer. Da es zugleich Pflichtfeld
     ist, liess sich das Formular ueberhaupt nicht abschicken: die Pruefung
     schlug immer fehl, und der einzige Weg hinaus war „Abbrechen". Genau das
     erlebt man als „das Formular laesst sich nicht schliessen".

     Abgerufen wird nur, solange ein Formular offen ist, das ein `oeCode`-Feld
     hat — der Drawer haengt ueber der ganzen Anwendung und soll nicht bei
     jedem Seitenaufruf eine Liste ziehen. Eine ausdrueckliche Angabe von
     aussen hat weiterhin Vorrang. */
  const brauchtOe = zustand
    ? (formularVon(zustand.key)?.gruppen ?? [])
      .some(([, felder]) => felder.some((f) => f.k === 'oeCode'))
    : false;

  const oeAbruf = useListe<{ code: string }>('organisationseinheiten', mandantAusSpeicher(), {
    seite: 1, groesse: 200, sortierung: 'code', richtung: 'auf',
    aus: !brauchtOe || organisationseinheiten !== undefined,
  });

  const oeListe: string[] = organisationseinheiten
    ?? (oeAbruf.daten?.zeilen ?? []).map((o: { code: string }) => o.code);

  /* --- Owner: vorschlagen, nicht vorschreiben -----------------------------
   *
   * Wer eine Applikation anlegt, tippte den Owner bisher frei ein — obwohl
   * im Bestand längst steht, welche es gibt. Zwei Schreibweisen desselben
   * Menschen sind dann zwei Owner, und keine Auswertung führt sie wieder
   * zusammen.
   *
   * Bewusst eine **Vorschlagsliste** und kein Auswahlfeld: ein Owner, den es
   * noch nicht gibt, muss sich eintragen lassen. Ein geschlossenes Feld
   * hieße, dass man die erste Applikation eines neuen Verantwortlichen nicht
   * anlegen kann — und der Ausweg wäre, irgendjemanden einzutragen.
   *
   * Die Werte kommen aus derselben Quelle wie die Spaltenfilter
   * (`/api/<sammlung>/_werte`), also **serverseitig gezählt** über den
   * ganzen Bestand und nicht über die geladene Seite.
   */
  const ownerFeld = zustand
    ? (formularVon(zustand.key)?.gruppen ?? [])
      .flatMap(([, felder]) => felder)
      .find((f) => OWNERFELDER.has(f.k))?.k ?? null
    : null;
  const ownerQuelle = zustand ? OWNERQUELLE[zustand.key] ?? null : null;

  const ownerAbruf = useWerte(
    ownerQuelle?.sammlung ?? 'assets',
    mandantAusSpeicher(),
    ownerFeld && ownerQuelle ? ownerQuelle.feld : null,
    {},
  );
  /* Auswahlfelder mit Werten aus dem Bestand (`werteAus`) — hoechstens eines
     je Formular, das genuegt heute. */
  const werteFeld = zustand
    ? (formularVon(zustand.key)?.gruppen ?? []).flatMap(([, f]) => f).find((f) => f.werteAus) ?? null
    : null;
  const bestandsAbruf = useWerte(
    werteFeld?.werteAus?.sammlung ?? 'assets',
    mandantAusSpeicher(),
    werteFeld?.werteAus?.feld ?? null,
    werteFeld?.werteAus?.filter ?? {},
  );
  const bestandsWerte: Record<string, string[]> = werteFeld
    ? { [werteFeld.k]: (bestandsAbruf.daten?.werte ?? []).map((w) => w.wert).filter((w) => w && w !== '—') }
    : {};

  const ownerListe: string[] = (ownerAbruf.daten?.werte ?? [])
    .map((w) => w.wert)
    .filter((w) => w && w !== '—');

  const oeffnen = useCallback<DrawerApi['oeffnen']>((key, vorbelegung, bearbeiten, bezug) => {
    /* Vorbelegt wird das **gefilterte** Formular. Ein ausgeblendetes Feld
       bekaeme sonst einen Anfangswert, den niemand sieht — und schickte ihn
       beim Speichern mit. */
    const kat = formularVon(key);
    if (!kat) return;
    const anfang = anfangswerte(kat, vorbelegung);
    setzeZustand({
      key,
      werte: anfang,
      anfang: { ...anfang },
      fehler: {},
      bearbeiten: !!bearbeiten,
      bezug: bezug ?? null,
      nachfrage: false,
      rollen: LEERER_PLAN,
      ersetzen: false,
    });
  }, [formularVon]);

  const api = useMemo(() => ({ oeffnen }), [oeffnen]);

  /**
   * Abbrechen — ohne Aenderung sofort, mit Aenderung erst nach Rueckfrage.
   *
   * Die Rueckfrage war ein `confirm(...)`. Der Browser zeichnet das als Leiste
   * am oberen Fensterrand, ausserhalb der Anwendung und ohne deren Gestaltung;
   * sie sperrt ausserdem den ganzen Reiter. Fuer eine Entscheidung, die ein
   * Formular betrifft, gehoert die Frage an das Formular. Sie steht jetzt in
   * dessen Fussleiste, dort wo auch die Schaltflaeche sitzt, die sie ausgeloest
   * hat.
   */
  const abbrechen = useCallback(() => {
    setzeZustand((st) => {
      if (!st) return null;
      /* Gewählte Rollen sind eine Eingabe wie jedes Feld — wer zwanzig
         angehakt hat, verliert sie nicht ohne Rückfrage. */
      if (!istGeaendert(st)) return null;
      return { ...st, nachfrage: true };
    });
  }, []);

  /** „Weiter bearbeiten" — die Rueckfrage verschwindet, das Formular bleibt. */
  const weiterBearbeiten = useCallback(() => {
    setzeZustand((st) => (st ? { ...st, nachfrage: false } : null));
  }, []);

  /**
   * „Als Kopie anlegen" — aus einem bestehenden Asset ein neues machen.
   *
   * Gemeldet am 30.09.2026 als „Cut & Paste fuer Felder (z. B. neue
   * Anwendung)": wer eine Anwendung anlegt, die einer vorhandenen fast
   * gleicht, tippte bisher alles neu. Jetzt oeffnet dasselbe Formular als
   * **Neuanlage** mit allen Werten, der Name bekommt „ (Kopie)". Die Kennung
   * vergibt die Schnittstelle neu; das bestehende Asset bleibt unberuehrt.
   * Dasselbe Muster wie „Als Vorlage kopieren" bei den Rollen.
   */
  const alsKopie = useCallback(() => {
    setzeZustand((st) => {
      if (!st) return st;
      const werte = { ...st.werte };
      /* Das Namensfeld aus der Abbildung — bei Hardware heisst es
         `bezeichnung`, und dort blieb die Kopie bis zum 07.10.2026 ohne
         Zusatz und damit namensgleich. */
      const namensfeld = namensfeldAusFormular(st.key)
        ?? ['name', 'pfad', 'adresse'].find((k) => k in werte);
      if (namensfeld && werte[namensfeld]) werte[namensfeld] = werte[namensfeld] + ' (Kopie)';
      /* Die Rollenauswahl geht nicht mit: sie bezog sich auf das alte Asset. */
      return {
        ...st, werte, anfang: { ...werte }, fehler: {}, bearbeiten: false, bezug: null,
        nachfrage: false, rollen: LEERER_PLAN, ersetzen: false,
      };
    });
  }, []);

  /** Die Rollenauswahl ändern — stabil, damit der Baustein nicht bei jedem Zeichnen neu rechnet. */
  const planSetzen = useCallback((rollen: Rollenplan) => {
    setzeZustand((st) => (st ? { ...st, rollen, fehler: { ...st.fehler, __rollen: undefined } } : st));
  }, []);

  /** „Ersetzen durch …" öffnen oder zurück zum Formular. */
  const ersetzenUmschalten = useCallback((an: boolean) => {
    setzeZustand((st) => (st ? { ...st, ersetzen: an, nachfrage: false } : st));
  }, []);

  /* --- Gleichzeitiges Arbeiten ------------------------------------------
   *
   * Zwei Dinge, beide seit dem 07.10.2026 (HOWOGE, Punkt 19):
   *
   * 1. Ein Hinweis, sobald jemand anders an diesem Satz schreibt — oder an
   *    den Zuordnungen dieses Assets. Ohne eigene Eingaben wird still neu
   *    geladen; mit eigenen steht „Neu laden / Weiterarbeiten" da.
   * 2. Bei 409 der Konfliktweg im Fenster, statt nur einer Meldung. Vorher
   *    hiess „bitte neu laden": schliessen, die Zeile suchen, alles neu
   *    tippen. */
  const konfliktweg = useKonfliktweg(mandantAusSpeicher());
  const sammlungJetzt = zustand ? ABBILDUNGEN[zustand.key]?.sammlung : undefined;
  const objektartJetzt = sammlungJetzt ? OBJEKTART[sammlungJetzt] : undefined;
  /* Zwei Beobachtungen, weil zweierlei zu tun ist: eine Änderung am Satz
     selbst betrifft das Formular, eine an den Zuordnungen des Assets nur den
     Abschnitt „Rollen zuordnen" (07.10.2026, Browsertest: eine fremde
     Zuordnung am offenen Asset blieb ohne jede Wirkung). */
  const beobachtet: Beobachtet | null = zustand?.bearbeiten && zustand.bezug && objektartJetzt
    ? { objektart: objektartJetzt, objektId: zustand.bezug.schluessel }
    : null;
  const fremd = useFremdaenderung(beobachtet);
  const nfJetzt = zustand ? namensfeldAusFormular(zustand.key) : null;
  const assetBeobachtet: AssetKennung | null = zustand?.bearbeiten && sammlungJetzt === 'assets'
    ? (zustand.bezug?.asset ?? {
      name: (nfJetzt && zustand.anfang[nfJetzt]) || '',
      assetTyp: assetTypAusFormular(zustand.key, zustand.anfang) ?? '',
      kategorie: kategorieAusFormular(zustand.key, zustand.anfang),
    })
    : null;
  const fremdRollen = useFremdaenderung(assetBeobachtet && assetBeobachtet.name
    ? {
      /* Keine Zuordnung heißt so — es zählt allein das Asset der Zeile. */
      objektart: 'Zuordnung', objektId: '',
      asset: {
        name: assetBeobachtet.name,
        assetTyp: assetBeobachtet.assetTyp,
        kategorie: assetBeobachtet.kategorie,
      },
    }
    : null);

  /** Den Abschnitt „Rollen zuordnen" neu laden — die Zahlen und Häkchen. */
  const rollenNeuLaden = useCallback((auswahlVerwerfen: boolean) => {
    if (auswahlVerwerfen) {
      setzeZustand((st) => (st ? { ...st, rollen: LEERER_PLAN } : st));
    }
    bestandGeaendert('zuordnungen', { still: true, fremd: true });
  }, []);

  /**
   * Den gespeicherten Stand holen und das Formular damit neu belegen.
   *
   * Verwirft die eigenen Eingaben **und** die Rollenauswahl — beide bezogen
   * sich auf einen Stand, den es nicht mehr gibt.
   */
  const neuLaden = useCallback(async (): Promise<boolean> => {
    const st = zustand;
    const bezugAlt = st?.bezug;
    if (!st || !bezugAlt) return false;
    const sammlung = ABBILDUNGEN[st.key]?.sammlung;
    const kat = formularVon(st.key);
    if (!sammlung || !kat) return false;
    try {
      const satz = await holen<Record<string, unknown>>(
        `/api/${sammlung}/${encodeURIComponent(bezugAlt.schluessel)}`,
        { mandant: mandantAusSpeicher(), token: await tokenHolen() },
      );
      const anfang = anfangswerte(kat, vorbelegungAusSatz(st.key, satz));
      const asset: AssetKennung | undefined = sammlung === 'assets'
        ? {
          name: String(satz['name'] ?? ''),
          assetTyp: String(satz['assetTyp'] ?? bezugAlt.asset?.assetTyp ?? ''),
          kategorie: String(satz['kategorie'] ?? ''),
        }
        : undefined;
      const bezugNeu: Bezug = {
        schluessel: bezugAlt.schluessel,
        version: String(satz['__version'] ?? bezugAlt.version),
        ...(asset ? { asset } : {}),
      };
      setzeZustand((alt) => (alt && alt.bezug?.schluessel === bezugAlt.schluessel ? {
        ...alt,
        werte: anfang,
        anfang: { ...anfang },
        fehler: {},
        nachfrage: false,
        rollen: LEERER_PLAN,
        bezug: bezugNeu,
      } : alt));
      return true;
    } catch (f) {
      melden('Nicht neu geladen', f instanceof Error ? f.message : 'Unbekannter Fehler.', 'fehler');
      return false;
    }
  }, [zustand, formularVon, melden]);

  /* Ohne eigene Eingaben gibt es nichts zu verlieren: still neu laden.
     Ausgelöst nur vom Eintrag, nicht von jeder Eingabe. */
  const fremdEintrag = fremd.eintrag;
  const quittieren = fremd.quittieren;
  useEffect(() => {
    if (!fremdEintrag || !zustand || laeuft) return;
    if (istGeaendert(zustand) || zustand.ersetzen) return;
    quittieren();
    void neuLaden();
  }, [fremdEintrag]);

  /* Fremde Zuordnungen am offenen Asset: ohne eigene Rollenauswahl still
     nachladen, sonst der Hinweis. Die eigene Auswahl bleibt dabei sicher —
     entfernt wird nur, was jemand hier abgewählt hat, mit der Version von
     diesem Moment. */
  const fremdRollenEintrag = fremdRollen.eintrag;
  const rollenQuittieren = fremdRollen.quittieren;
  useEffect(() => {
    if (!fremdRollenEintrag || !zustand) return;
    if (planBelegt(zustand.rollen)) return;
    rollenQuittieren();
    rollenNeuLaden(false);
  }, [fremdRollenEintrag]);

  /** „Verwerfen" — jetzt wirklich schliessen. */
  const verwerfen = useCallback(() => setzeZustand(null), []);

  // Escape schliesst, Tabulator bleibt im Fenster (Fokusfalle wie im Entwurf).
  useEffect(() => {
    if (!zustand) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Steht die Rueckfrage, hebt Escape sie auf — es ist die zurueck-
        // haltende der beiden Antworten. Escape darf nicht verwerfen: dieselbe
        // Taste wuerde sonst je nach Lage etwas anlegen oder etwas wegwerfen.
        if (zustand.nachfrage) weiterBearbeiten(); else abbrechen();
        return;
      }
      if (e.key !== 'Tab') return;
      const dlg = fenster.current;
      if (!dlg) return;
      const f = Array.from(
        dlg.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      const erst = f[0];
      const letzt = f[f.length - 1];
      if (!erst || !letzt) return;
      if (e.shiftKey && document.activeElement === erst) { e.preventDefault(); letzt.focus(); }
      else if (!e.shiftKey && document.activeElement === letzt) { e.preventDefault(); erst.focus(); }
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [zustand, abbrechen, weiterBearbeiten]);

  useEffect(() => {
    if (zustand) fenster.current?.querySelector<HTMLElement>('input,select,textarea')?.focus();
  }, [zustand?.key]);

  const setzenFeld = (k: string, v: string) =>
    setzeZustand((s) => {
      if (!s) return s;
      const werte = { ...s.werte, [k]: v };
      /* Abhaengige Vorgaben mitziehen — aber nur, solange dort noch die alte
         Vorgabe steht. Was jemand selbst gewaehlt hat, bleibt. */
      const felder = (formularVon(s.key)?.gruppen ?? []).flatMap(([, f]) => f);
      for (const fd of felder) {
        if (fd.vorgabeJe?.[0] !== k) continue;
        const alt = vorgabeFuer(fd, s.werte);
        const neu = vorgabeFuer(fd, werte);
        if (neu !== undefined && (s.werte[fd.k] === '' || s.werte[fd.k] === alt)) werte[fd.k] = neu;
      }
      /* Passt ein gewaehlter Wert nach dem Wechsel nicht mehr — „schreiben"
         an einer Anwendung —, rueckt die Vorgabe der neuen Lage nach. */
      for (const fd of felder) {
        if (fd.optionenJe?.[0] !== k) continue;
        const erlaubt = optionenFuer(fd, werte);
        const jetzt = werte[fd.k] ?? '';
        if (jetzt && !erlaubt.includes(jetzt)) werte[fd.k] = vorgabeFuer(fd, werte) ?? erlaubt[0] ?? '';
      }
      return { ...s, werte, fehler: { ...s.fehler, [k]: undefined } };
    });

  const pruefenFeld = (fd: Feld) =>
    setzeZustand((s) => s && ({ ...s, fehler: { ...s.fehler, [fd.k]: feldFehler(fd, s.werte[fd.k]) || undefined } }));

  const allePruefen = (kat: Formular, werte: Record<string, string>) => {
    const fehler: Record<string, string> = {};
    kat.gruppen.forEach(([, felder]) =>
      felder.forEach((fd) => {
        const m = feldFehler(fd, werte[fd.k]);
        if (m) fehler[fd.k] = m;
      }),
    );
    return fehler;
  };

  const speichern = async (weiter: boolean, ueberBezug?: Bezug) => {
    if (!zustand) return;
    const bezugRoh = ueberBezug ?? zustand.bezug;
    /* Ein Asset ohne mitgegebene Kennung — geoeffnet von einer Stelle, die
       sie nicht kennt — bekommt sie aus den Anfangswerten des Formulars. */
    const nf = namensfeldAusFormular(zustand.key);
    const bezugJetzt: Bezug | null = bezugRoh && !bezugRoh.asset
      && ABBILDUNGEN[zustand.key]?.sammlung === 'assets'
      ? {
        ...bezugRoh,
        asset: {
          name: (nf && zustand.anfang[nf]) || '',
          assetTyp: assetTypAusFormular(zustand.key, zustand.anfang) ?? '',
          kategorie: kategorieAusFormular(zustand.key, zustand.anfang),
        },
      }
      : bezugRoh;
    /* Geprueft wird ebenfalls das gefilterte Formular: ein unsichtbares
       Pflichtfeld blockierte sonst das Speichern, ohne dass irgendwo stuende,
       warum. */
    const kat = formularVon(zustand.key);
    if (!kat) return;
    const fehler: Record<string, string> = allePruefen(kat, zustand.werte);
    /* Die Rollenauswahl wird mitgeprüft — eine Entfernung ohne Grund liefe
       sonst erst an der Schnittstelle auf. */
    const istAsset = ABBILDUNGEN[zustand.key]?.sammlung === 'assets';
    const planText = istAsset ? planFehler(zustand.rollen) : null;
    if (planText) fehler['__rollen'] = planText;
    if (Object.keys(fehler).length) {
      setzeZustand({ ...zustand, fehler });
      // Zum ersten fehlerhaften Feld springen — die Zusammenfassung steht oben,
      // das Feld kann weit darunter liegen.
      const erstes = kat.gruppen
        .flatMap(([, felder]) => felder)
        .find((fd) => fehler[fd.k]);
      const ziel = erstes ? 'f-' + erstes.k : planText ? 'rz-grund' : null;
      if (ziel) {
        window.setTimeout(() => {
          const feld = document.getElementById(ziel) ?? document.getElementById('rz-titel');
          feld?.focus();
          feld?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
      }
      return;
    }
    // Das erste Feld der ersten Gruppe traegt den Namen — so benennt der
    // Entwurf den Datensatz in der Rueckmeldung.
    const erstes = kat.gruppen[0]?.[1][0]?.k;
    const name = (erstes && zustand.werte[erstes]) || kat.titel;

    /*
     * Ohne Empfaenger wird nichts gespeichert — und dann darf hier auch kein
     * Erfolg gemeldet werden.
     *
     * Der Drawer wird in `main.tsx` ohne `onSpeichern` eingehaengt, weil es im
     * Backend bisher keinen Schreibpfad gibt. Frueher lief der Aufruf ins
     * Leere und die Meldung behauptete trotzdem, der Datensatz „stehe in allen
     * Auswahllisten zur Verfuegung". Eine Berechtigungsaenderung, die nur
     * scheinbar erfolgt ist, richtet mehr Schaden an als eine, die sichtbar
     * fehlschlaegt: niemand sucht nach dem Fehler.
     */
    if (!onSpeichern) {
      melden(
        'Noch nicht gespeichert',
        'Die Eingaben sind vollständig, aber der Schreibweg zur Datenbank ist ' +
          'noch nicht angebunden. „' + name + '“ wurde nicht übernommen.',
        'fehler',
      );
      return;
    }

    /*
     * Die Rueckmeldung gehoert dem Empfaenger, nicht diesem Baustein.
     *
     * Bis zum 13.08.2026 meldete der Drawer hier selbst „Angelegt" — und zwar
     * **bevor** `onSpeichern` fertig war. Der Aufruf ist asynchron: die
     * Meldung stand, waehrend die Anfrage noch lief. Wurde sie abgewiesen,
     * standen zwei Meldungen uebereinander, „Angelegt" und „Nicht angelegt",
     * und die erste war die groessere Schrift.
     *
     * Genau die Klasse Falschmeldung, gegen die `bausteine/nochNicht.ts`
     * geschrieben wurde. Der Empfaenger weiss als einziger, ob geschrieben,
     * zur Freigabe eingereicht oder abgewiesen wurde — er sagt es auch.
     */
    /* Gewartet wird, und zwar sichtbar.
     *
     * Bis zum 27.08.2026 schloss der Drawer **sofort** und der Schreibvorgang
     * lief daneben weiter. Der Grund war gut — „ein Formular, das nach dem
     * Speichern noch sekundenlang offensteht, wird ein zweites Mal
     * abgeschickt" — aber der Preis war hoch: eine Abweisung durch die
     * Schnittstelle kam an, wenn das Fenster mit allen Eingaben schon weg
     * war. Wer zwanzig Felder gefuellt hatte, fuellte sie erneut.
     *
     * Gegen das Doppelabschicken hilft nicht das Schliessen, sondern das
     * Sperren: `laeuft` nimmt beiden Knoepfen die Wirkung, solange die
     * Anfrage laeuft. */
    setzeLaeuft(true);
    konfliktweg.schliessen();
    let antwort: void | boolean | Speicherantwort;
    try {
      antwort = await onSpeichern(
        zustand.key, zustand.werte, zustand.bearbeiten, bezugJetzt,
        {
          rollen: istAsset ? zustand.rollen : null,
          felderGeaendert: Object.keys(zustand.werte)
            .some((k) => zustand.werte[k] !== zustand.anfang[k]),
        });
    } finally {
      setzeLaeuft(false);
    }
    if (antwort === false) return;
    if (antwort && typeof antwort === 'object') {
      if ('konflikt' in antwort) {
        /* Der Stand, auf dem ein „trotzdem speichern" aufsetzt, kommt frisch
           vom Server; das Protokoll sagt, wer was geändert hat. */
        const sammlung = ABBILDUNGEN[zustand.key]?.sammlung;
        const schluessel = bezugJetzt?.schluessel;
        const objektart = sammlung ? OBJEKTART[sammlung] : undefined;
        const behandelt = sammlung && schluessel
          ? await konfliktweg.auffangen(
            antwort.konflikt,
            async () => {
              const satz = await holen<{ __version?: string }>(
                `/api/${sammlung}/${encodeURIComponent(schluessel)}`,
                { mandant: mandantAusSpeicher(), token: await tokenHolen() },
              );
              return satz.__version ?? null;
            },
            objektart ? { objektart, objektId: schluessel } : null,
          )
          : false;
        if (!behandelt) {
          const f = antwort.konflikt;
          melden('Nicht gespeichert', f instanceof Error ? f.message : 'Unbekannter Fehler.', 'fehler');
        }
        return;
      }
      /* Offen bleiben — mit dem, was inzwischen feststeht. */
      const { bezug: neuBezug, rollen } = antwort;
      setzeZustand((st) => (st ? {
        ...st,
        ...(neuBezug ? { bezug: neuBezug, bearbeiten: true, anfang: { ...st.werte } } : {}),
        ...(rollen ? { rollen } : {}),
      } : st));
      return;
    }
    if (weiter) oeffnen(zustand.key, null, false);
    else setzeZustand(null);
  };

  /* Die Bausteine, die nur Assets haben: Rollenzuordnung und Ersetzen. */
  const istAssetJetzt = sammlungJetzt === 'assets';
  const namensfeld = zustand ? namensfeldAusFormular(zustand.key) : null;
  const bestandsAsset: AssetKennung | null = zustand?.bearbeiten && istAssetJetzt
    ? (zustand.bezug?.asset ?? {
      name: (namensfeld && zustand.anfang[namensfeld]) || '',
      assetTyp: assetTypAusFormular(zustand.key, zustand.anfang) ?? '',
      kategorie: kategorieAusFormular(zustand.key, zustand.anfang),
    })
    : null;
  const zuordnungsTyp = bestandsAsset?.assetTyp
    || (zustand ? assetTypAusFormular(zustand.key, zustand.werte) : null) || '';

  return (
    <DrawerKontext.Provider value={api}>
      {children}
      {zustand && (
        <Fenster
          zustand={zustand}
          konflikt={konfliktweg.konflikt}
          onKonfliktTrotzdem={(neueVersion) => {
            if (!zustand.bezug) return;
            const neu: Bezug = { ...zustand.bezug, version: neueVersion };
            setzeZustand((st) => (st ? { ...st, bezug: neu } : st));
            void speichern(false, neu);
          }}
          onKonfliktUebernehmen={() => {
            konfliktweg.schliessen();
            void neuLaden().then((ok) => {
              if (ok) {
                melden('Fremder Stand übernommen',
                  'Ihre Eingaben wurden verworfen. Angezeigt wird jetzt der Stand aus der Datenbank.',
                  'hinweis');
              }
            });
          }}
          livehinweis={fremd.eintrag ? (
            <Livehinweis eintrag={fremd.eintrag}
                         sache={istAssetJetzt ? 'Dieses Asset' : 'Dieser Eintrag'}
                         ungespeichert={istGeaendert(zustand)}
                         onNeuLaden={() => { fremd.quittieren(); void neuLaden(); }}
                         onWeiter={fremd.quittieren} />
          ) : fremdRollen.eintrag && planBelegt(zustand.rollen) ? (
            <Livehinweis eintrag={fremdRollen.eintrag}
                         sache={'Die Rollenzuordnung ' + artText(bestandsAsset?.assetTyp).dieser}
                         ungespeichert
                         verwirft="Ihre Rollenauswahl"
                         mitFeldern={false}
                         onNeuLaden={() => { fremdRollen.quittieren(); rollenNeuLaden(true); }}
                         onWeiter={fremdRollen.quittieren} />
          ) : null}
          kurzErsetzen={'Alle Rollenzuordnungen auf ' + artText(bestandsAsset?.assetTyp).andere
            + ' übertragen.'}
          rollenzuordnung={istAssetJetzt ? (
            <Rollenzuordnung
              mandant={mandantAusSpeicher()}
              bestand={bestandsAsset && bestandsAsset.name
                ? {
                  name: bestandsAsset.name,
                  assetTyp: bestandsAsset.assetTyp,
                  kategorie: bestandsAsset.kategorie,
                }
                : null}
              assetTyp={zuordnungsTyp}
              plan={zustand.rollen}
              onPlan={planSetzen}
              gesperrt={laeuft || DEMO}
            />
          ) : null}
          ersetzenweg={zustand.ersetzen && zustand.bezug && bestandsAsset ? (
            <Ersetzenweg
              mandant={mandantAusSpeicher()}
              formular={zustand.key}
              werte={zustand.werte}
              quelle={{
                assetId: zustand.bezug.schluessel,
                version: zustand.bezug.version,
                name: bestandsAsset.name,
                assetTyp: bestandsAsset.assetTyp,
                kategorie: bestandsAsset.kategorie,
              }}
              ungespeichert={istGeaendert(zustand)}
              onZurueck={() => ersetzenUmschalten(false)}
              onFertig={() => setzeZustand(null)}
            />
          ) : null}
          onErsetzen={istAssetJetzt && zustand.bearbeiten && zustand.bezug && !DEMO
            ? () => ersetzenUmschalten(true) : null}
          fensterRef={fenster}
          organisationseinheiten={oeListe}
          bestandsWerte={bestandsWerte}
          ownerListe={ownerListe}
          onAbbrechen={abbrechen}
          onWeiterBearbeiten={weiterBearbeiten}
          onAlsKopie={ABBILDUNGEN[zustand.key]?.sammlung === 'assets' && zustand.bearbeiten ? alsKopie : null}
          onVerwerfen={verwerfen}
          onSetzen={setzenFeld}
          onPruefen={pruefenFeld}
          onSpeichern={speichern}
          laeuft={laeuft}
        />
      )}
    </DrawerKontext.Provider>
  );
}

/* -------------------------------------------------------------- Fenster */

function Fenster({
  zustand, fensterRef, organisationseinheiten, bestandsWerte, ownerListe, onAbbrechen, onWeiterBearbeiten, onAlsKopie,
  onVerwerfen, onSetzen, onPruefen, onSpeichern, laeuft,
  konflikt, onKonfliktTrotzdem, onKonfliktUebernehmen, livehinweis, rollenzuordnung, ersetzenweg, onErsetzen,
  kurzErsetzen,
}: {
  zustand: DrawerZustand;
  /** Die Unterzeile im Kopf, solange „Ersetzen durch …" offen ist. */
  kurzErsetzen: string;
  /** Ein 409 beim Speichern — die Leiste steht im Formular. */
  konflikt: Konflikt | null;
  onKonfliktTrotzdem: (neueVersion: string) => void;
  onKonfliktUebernehmen: () => void;
  /** „Dieses Asset wurde um … geändert" — oder nichts. */
  livehinweis: ReactNode;
  /** Die Rollenauswahl unter den Feldern — nur bei Assets. */
  rollenzuordnung: ReactNode;
  /** Steht statt Formular und Fuss, solange „Ersetzen durch …" offen ist. */
  ersetzenweg: ReactNode;
  /** Nur beim Bearbeiten eines Assets: „Ersetzen durch …". */
  onErsetzen: (() => void) | null;
  fensterRef: React.RefObject<HTMLElement | null>;
  organisationseinheiten: string[];
  /** Werte aus dem Bestand je Feld (`werteAus`), zusaetzlich zu den festen Optionen. */
  bestandsWerte: Record<string, string[]>;
  /** Bekannte Verantwortliche aus dem Bestand — Vorschlag, keine Vorschrift. */
  ownerListe: string[];
  onAbbrechen: () => void;
  onWeiterBearbeiten: () => void;
  /** Nur beim Bearbeiten eines Assets: dasselbe als Neuanlage vorbelegt. */
  onAlsKopie: (() => void) | null;
  onVerwerfen: () => void;
  onSetzen: (k: string, v: string) => void;
  onPruefen: (fd: Feld) => void;
  onSpeichern: (weiter: boolean) => void;
  /** Solange gespeichert wird, nehmen die Knoepfe keine Klicks an. */
  laeuft: boolean;
}) {
  const module = useModule();
  const roh = FORMULARE[zustand.key];
  const kat = roh ? formularFuerModule(roh, module) : undefined;
  if (!kat) return null;

  // Fehlerzusammenfassung in Feldreihenfolge — dieselbe Reihenfolge, in der
  // die Felder im Formular stehen, damit die Sprunglinks nicht springen.
  const fehlerliste = kat.gruppen.flatMap(([, felder]) =>
    felder
      .filter((fd) => zustand.fehler[fd.k])
      .map((fd) => ({ anker: '#f-' + fd.k, text: fd.label + ': ' + zustand.fehler[fd.k] })),
  );
  if (zustand.fehler['__rollen']) {
    fehlerliste.push({ anker: '#rz-grund', text: 'Rollen: ' + zustand.fehler['__rollen'] });
  }

  const eingabeStil = (ungueltig: boolean, mono: boolean) =>
    'width:100%;height:34px;padding:0 10px;background:var(--surface-page);border:1px solid var(' +
    (ungueltig ? '--danger' : '--border-default') +
    ');border-radius:var(--r-md);color:var(--text-primary);font-size:13px;' +
    (mono ? 'font-family:var(--mono);' : '') + 'box-sizing:border-box';

  const titel = zustand.ersetzen
    ? kat.titel.replace(/ (anlegen|erfassen)$/, '') + ' ersetzen durch …'
    : zustand.bearbeiten
      ? kat.titel.replace(/ (anlegen|erfassen)$/, '') + ' bearbeiten'
      : kat.titel;

  return (
    <div style={stil(
      'position:fixed;inset:0;z-index:var(--z-fenster);display:flex;align-items:center;justify-content:center;padding:32px 16px',
    )}>
      <button
        onClick={onAbbrechen}
        aria-label="Formular schließen"
        style={stil('position:absolute;inset:0;background:rgb(0 0 0/.46);border:none;cursor:pointer')}
      />
      <aside
        ref={fensterRef}
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        style={stil(
          'position:relative;width:min(760px,100%);height:min(78vh,760px);min-width:min(440px,100%);min-height:320px;' +
          'max-width:100%;max-height:calc(100vh - 64px);resize:both;overflow:hidden;' +
          'display:flex;flex-direction:column;background:var(--surface-card);border:1px solid var(--border-subtle);' +
          'border-radius:var(--r-lg);box-shadow:0 24px 64px rgb(0 0 0/.28);' +
          'animation:einblenden .18s cubic-bezier(.2,0,0,1)',
        )}
      >
        <span aria-hidden="true" title="Größe ändern" style={stil(
          'position:absolute;right:2px;bottom:2px;width:16px;height:16px;cursor:nwse-resize;' +
          'border-right:2px solid var(--border-default);border-bottom:2px solid var(--border-default);' +
          'border-bottom-right-radius:4px;pointer-events:none',
        )} />

        <div style={stil(
          'flex:0 0 auto;display:flex;align-items:flex-start;gap:12px;padding:16px 20px 13px;' +
          'border-bottom:1px solid var(--border-subtle)',
        )}>
          <div style={stil('flex:1;min-width:0')}>
            <div style={stil(
              'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--text-tertiary)',
            )}>{kat.kicker}</div>
            <div style={stil('font-size:17px;font-weight:600;line-height:24px')}>{titel}</div>
            <div style={stil('margin-top:2px;font-size:12px;line-height:18px;color:var(--text-secondary)')}>
              {zustand.ersetzen ? kurzErsetzen : kat.kurz}
            </div>
          </div>
          <button
            onClick={onAbbrechen}
            aria-label="Schließen"
            className="knopf-still"
            style={stil(
              'width:32px;height:32px;display:grid;place-items:center;background:none;border:none;' +
              'border-radius:var(--r-md);color:var(--text-secondary);cursor:pointer',
            )}
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {ersetzenweg ?? (<>
        <div style={stil('flex:1;min-height:0;overflow-y:auto;padding:16px 20px 20px')}>
          {livehinweis}
          {konflikt && (
            <Konfliktleiste
              konflikt={konflikt}
              gegenstand={`„${zustand.anfang[kat.gruppen[0]?.[1][0]?.k ?? ''] || kat.titel}“`}
              laeuft={laeuft}
              onTrotzdem={onKonfliktTrotzdem}
              onUebernehmen={onKonfliktUebernehmen}
            />
          )}
          <Fehlerkopf liste={fehlerliste} />

          {kat.gruppen
            .map(([label, alle]): [string, Feld[]] =>
              [label, alle.filter((fd) => feldSichtbar(fd, zustand.werte))])
            .filter(([, felder]) => felder.length > 0)
            .map(([label, felder]) => (
            <div key={label} style={stil('margin-bottom:20px')}>
              <div style={stil(
                'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
                'color:var(--text-tertiary);margin-bottom:10px',
              )}>{label}</div>
              <div style={stil('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px')}>
                {felder.map((fd) => {
                  const fehlerText = zustand.fehler[fd.k] || null;
                  /* Hier stand ein zweites `fehlerliste.push(...)`. Die Liste
                     ist oben bereits vollstaendig aufgebaut; da React die
                     Kindkomponente erst nach dem Elementbaum aufruft, sah
                     `Fehlerkopf` jeden Fehler doppelt — „2 Felder sind noch
                     nicht in Ordnung", wenn genau eines fehlte, dazu doppelte
                     React-Schluessel. */
                  const mono =
                    fd.k === 'gruppenName' || fd.k === 'groupId' || fd.k === 'pfad' ||
                    (fd.k === 'name' && zustand.key === 'gruppe');
                  const optionen =
                    fd.k === 'oeCode' && organisationseinheiten.length
                      ? organisationseinheiten
                      : [...new Set([...optionenFuer(fd, zustand.werte), ...(bestandsWerte[fd.k] ?? [])])];
                  const wert = zustand.werte[fd.k] || '';

                  return (
                    <div
                      key={fd.k}
                      style={stil(
                        'grid-column:' + (fd.breit || fd.typ === 'fliess' ? 'span 2' : 'span 1') + ';min-width:0',
                      )}
                    >
                      <label htmlFor={'f-' + fd.k} title={erklaerung(fd.label)} style={stil(
                        'display:block;font-size:13px;line-height:18px;font-weight:500;margin-bottom:4px',
                      )}>
                        {fd.label}
                        <span style={stil(fd.pflicht ? 'color:var(--danger)' : 'display:none')}> *</span>
                      </label>

                      {fd.typ === 'auswahl' ? (
                        <select
                          id={'f-' + fd.k}
                          value={wert}
                          onChange={(e) => onSetzen(fd.k, e.target.value)}
                          aria-invalid={!!fehlerText}
                          style={stil(eingabeStil(!!fehlerText, false))}
                        >
                          {/* Leerwert sichtbar machen.
                              `anfangswerte` setzt Pflichtfelder auf '' — ohne
                              eine Option mit diesem Wert zeigt der Browser die
                              **erste** Option an. Das Feld sah damit ausgefüllt
                              aus („CLP"), meldete beim Speichern aber
                              „Pflichtfeld", und niemand fand den Widerspruch:
                              was dasteht, ist nicht, was gilt. */}
                          {wert === '' && (
                            <option value="">
                              {fd.pflicht ? '— bitte wählen —' : '— ohne Angabe —'}
                            </option>
                          )}
                          {/* Ein gespeicherter Wert, den die Liste nicht kennt, steht
                              als eigene Option da. Sonst zeigte der Browser die
                              erste Option — ein „AVD" stand als
                              „Standardanwendungen" im Formular, gespeichert blieb
                              AVD (30.09.2026). Was dasteht, soll gelten. */}
                          {wert !== '' && !optionen.includes(wert) && (
                            <option value={wert}>{wert}</option>
                          )}
                          {optionen.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : fd.typ === 'fliess' ? (
                        <textarea
                          id={'f-' + fd.k}
                          value={wert}
                          onChange={(e) => onSetzen(fd.k, e.target.value)}
                          onBlur={() => onPruefen(fd)}
                          placeholder={fd.platzhalter}
                          rows={3}
                          aria-invalid={!!fehlerText}
                          style={stil(
                            eingabeStil(!!fehlerText, false).replace(
                              'height:34px',
                              'min-height:70px;padding:8px 10px;line-height:19px;resize:vertical',
                            ),
                          )}
                        />
                      ) : (
                        <>
                          <input
                            id={'f-' + fd.k}
                            value={wert}
                            onChange={(e) => onSetzen(fd.k, e.target.value)}
                            onBlur={() => onPruefen(fd)}
                            placeholder={fd.platzhalter}
                            aria-invalid={!!fehlerText}
                            /* Nur bei Ownerfeldern eine Liste: sonst schlägt
                               der Browser eigene, früher getippte Werte vor,
                               und die haben mit diesem Bestand nichts zu
                               tun. */
                            {...(OWNERFELDER.has(fd.k) && ownerListe.length
                              ? { list: 'owner-vorschlaege' }
                              : { autoComplete: 'off' as const })}
                            spellCheck={false}
                            style={stil(eingabeStil(!!fehlerText, !!mono))}
                          />
                          {/* Kein Hinweistext unter dem Ownerfeld.
                              Er zählte die bekannten Owner auf („2 bekannte
                              Owner im Bestand …") und erklärte eine Mechanik,
                              die sich beim ersten Tippen von selbst zeigt:
                              die Vorschlagsliste klappt auf. Am 01.09.2026
                              abbestellt. Die Liste selbst bleibt — sie steht
                              oben am `list`-Attribut. */}
                        </>
                      )}

                      {fehlerText && (
                        <div style={stil('margin-top:4px;font-size:12px;color:var(--danger)')}>{fehlerText}</div>
                      )}
                      {!fehlerText && fd.musterText && (
                        <div style={stil('margin-top:4px;font-size:12px;color:var(--text-tertiary)')}>
                          {fd.musterText}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {rollenzuordnung}

          {/* Einmal für alle Ownerfelder des Formulars — ein `datalist` je
              Feld wäre dieselbe Liste mehrfach im Baum. */}
          {ownerListe.length > 0 && (
            <datalist id="owner-vorschlaege">
              {ownerListe.map((o) => <option key={o} value={o} />)}
            </datalist>
          )}
        </div>

        <div style={stil(
          /* `flex-wrap`: mit „Als Kopie anlegen" stehen beim Bearbeiten drei
             Knoepfe im Fuss — bei 375 px sind das 438 px (30.09.2026). */
          'flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding:12px 20px;' +
          'border-top:1px solid var(--border-subtle);background:var(--surface-card)',
        )}>
          {zustand.nachfrage ? (
            /* Die Rueckfrage ersetzt die Fussleiste, statt sich darueber zu
               legen. So bleibt genau eine Handlungszeile sichtbar, und die
               Frage steht dort, wo die ausloesende Schaltflaeche stand.
               `role="alertdialog"` meldet sie Vorleseprogrammen als das, was
               sie ist — eine Entscheidung, keine Mitteilung. */
            <div role="alertdialog" aria-label="Eingaben verwerfen?" style={stil(
              'display:flex;align-items:center;gap:10px;flex-wrap:wrap;width:100%',
            )}>
              <span style={stil('display:flex;align-items:center;gap:8px;font-size:13px')}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="var(--warning)" strokeWidth="2.2" strokeLinecap="round"
                     style={stil('flex:0 0 16px')}>
                  <path d="M12 3l9.5 17H2.5L12 3zM12 9v5M12 17.2v.1" />
                </svg>
                Ihre Eingaben sind noch nicht gespeichert.
              </span>
              <span style={stil('flex:1')} />
              <button onClick={onWeiterBearbeiten} autoFocus style={stil(
                'height:34px;padding:0 14px;background:var(--surface-card);' +
                'border:1px solid var(--border-default);border-radius:var(--r-md);' +
                'font-size:13px;color:var(--text-primary);cursor:pointer',
              )}>Weiter bearbeiten</button>
              <button onClick={onVerwerfen} style={stil(
                'height:34px;padding:0 16px;background:var(--danger);color:var(--text-on-brand);' +
                'border:none;border-radius:var(--r-md);font-size:13px;font-weight:500;cursor:pointer',
              )}>Verwerfen</button>
            </div>
          ) : (
            <>
              <button onClick={onAbbrechen} style={stil(
                'height:34px;padding:0 14px;background:var(--surface-card);border:1px solid var(--border-default);' +
                'border-radius:var(--r-md);font-size:13px;color:var(--text-primary);cursor:pointer',
              )}>Abbrechen</button>
              {/* In der Demo sagt die Fusszeile, warum nichts gespeichert
                  wird — der Platzhalter wird zur Auskunft, die Knoepfe bleiben
                  sichtbar, aber abgeschaltet. */}
              {DEMO ? (
                <span style={stil(
                  'flex:1;align-self:center;font-size:12px;line-height:16px;color:var(--text-secondary)',
                )}>{DEMO_HINWEIS}</span>
              ) : (
                <span style={stil('flex:1')} />
              )}
              {/* Waehrend geschrieben wird, nehmen beide Knoepfe nichts an.
                  Das ersetzt das sofortige Schliessen, das bis zum 27.08.2026
                  gegen das Doppelabschicken half — und dabei alle Eingaben
                  mitnahm, wenn die Schnittstelle abwies. */}
              {onAlsKopie && (
                <button onClick={onAlsKopie} disabled={laeuft}
                        title="Ein neues Asset mit denselben Angaben anlegen" style={stil(
                  'height:34px;padding:0 14px;background:var(--surface-card);border:1px solid var(--border-default);' +
                  'border-radius:var(--r-md);font-size:13px;color:var(--text-primary);' +
                  (laeuft ? 'cursor:not-allowed;opacity:.55' : 'cursor:pointer'),
                )}>Als Kopie anlegen</button>
              )}
              {/* Seit dem 07.10.2026: alle Rollen auf ein anderes Asset
                  übertragen (HOWOGE, Punkt 16). */}
              {onErsetzen && (
                <button onClick={onErsetzen} disabled={laeuft}
                        title="Alle Rollen auf ein anderes Asset gleicher Art übertragen" style={stil(
                  'height:34px;padding:0 14px;background:var(--surface-card);border:1px solid var(--border-default);' +
                  'border-radius:var(--r-md);font-size:13px;color:var(--text-primary);' +
                  (laeuft ? 'cursor:not-allowed;opacity:.55' : 'cursor:pointer'),
                )}>Ersetzen durch …</button>
              )}
              <button onClick={() => onSpeichern(true)} disabled={DEMO || laeuft}
                      title={DEMO ? DEMO_HINWEIS : undefined} style={stil(
                'height:34px;padding:0 14px;background:var(--surface-card);border:1px solid var(--border-default);' +
                'border-radius:var(--r-md);font-size:13px;color:var(--text-primary);' +
                (DEMO || laeuft ? 'cursor:not-allowed;opacity:.55' : 'cursor:pointer'),
              )}>Speichern und neu</button>
              <button onClick={() => onSpeichern(false)} disabled={DEMO || laeuft}
                      title={DEMO ? DEMO_HINWEIS : undefined} style={stil(
                'height:34px;padding:0 16px;background:var(--brand-strong);color:var(--text-on-brand);border:none;' +
                'border-radius:var(--r-md);font-size:13px;font-weight:500;' +
                (DEMO || laeuft ? 'cursor:not-allowed;opacity:.55' : 'cursor:pointer'),
              )}>{laeuft ? 'Wird gespeichert…'
                : zustand.bearbeiten ? 'Änderungen speichern' : 'Speichern'}</button>
            </>
          )}
        </div>
        </>)}
      </aside>
    </div>
  );
}

/* --------------------------------------------- Fehlerkopf mit Sprunglinks */

function Fehlerkopf({ liste }: { liste: { anker: string; text: string }[] }) {
  if (!liste.length) return null;
  const gezeigt = liste;
  return (
    <div role="alert" style={stil(
      'margin-bottom:16px;padding:11px 13px;background:var(--danger-subtle);' +
      'border:1px solid var(--danger);border-radius:var(--r-md)',
    )}>
      <div style={stil('font-size:13px;font-weight:600;color:var(--danger)')}>
        {gezeigt.length === 1
          ? 'Ein Feld ist noch nicht in Ordnung:'
          : gezeigt.length + ' Felder sind noch nicht in Ordnung:'}
      </div>
      <div style={stil('display:flex;flex-direction:column;gap:3px;margin-top:6px')}>
        {gezeigt.map((f) => (
          <a
            key={f.anker}
            href={f.anker}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(f.anker.slice(1))?.focus();
            }}
            style={stil('font-size:12px;color:var(--danger)')}
          >
            {f.text}
          </a>
        ))}
      </div>
    </div>
  );
}

